import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';

/**
 * Team365 → Backend → Automations (Jenn, 2026-10-27): "Where do I see the
 * automations/processes we've built to review them and make sure they are
 * operating?" Every automation in plain words, whether it's working, when it
 * last ran. Quiet on purpose: no red alarms (Jenn's rule); anything that needs
 * a look also becomes a Team365 To-Do by itself (automationWatch job).
 *
 * Oct 29 (Jenn: "It needs to work like FEA Create, so you can fix things
 * yourself when Claude isn't available"): open one to see its steps as
 * bubbles with how many people are at each step (tap the number to see who,
 * in a side panel); buttons Team365 can use (Refresh, Run now, Pause/Resume,
 * Stop all emails, Stop all texts, Restart the app); and automations Team365
 * makes itself from building blocks.
 */
const STATUS = {
  ok:      { dot: 'bg-reset-sage',       text: 'Working' },
  problem: { dot: 'bg-reset-gold',       text: 'Had a problem' },
  missed:  { dot: 'bg-reset-terracotta', text: "Didn't run on time" },
  paused:  { dot: 'bg-gray-400',         text: 'Paused' },
  not_yet: { dot: 'bg-gray-300',         text: 'Waiting for its first run' },
  waiting: { dot: 'bg-gray-300',         text: 'Waiting until it is next needed' },
};
const ZONE = { 'America/Halifax': 'AT', 'America/Glace_Bay': 'AT', 'America/New_York': 'ET' };
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const STEP_LABEL = { start: 'Starts', who: 'Who', send: 'Does', check: 'Checks', next: 'Then' };

const clock = (h, m) => {
  const hh = Number(h), mm = String(m).padStart(2, '0');
  return `${hh % 12 || 12}:${mm} ${hh < 12 ? 'am' : 'pm'}`;
};
// Plain words for a schedule ("Every day at 8:00 am AT"). Anything unusual is shown as it is.
export const describeSchedule = (expr, tz) => {
  const z = ZONE[tz] || 'AT';
  const [m, h, dom, mon, dow] = String(expr || '').split(/\s+/);
  if (!dow) return expr || '';
  if (m.startsWith('*/') && h === '*') return `Every ${m.slice(2)} minutes`;
  if (/^\d+$/.test(m) && h === '*') return `Every hour at :${m.padStart(2, '0')}`;
  if (/^\d+$/.test(m) && /^\d+$/.test(h) && dom === '*' && mon === '*') {
    if (dow === '*') return `Every day at ${clock(h, m)} ${z}`;
    if (/^\d$/.test(dow)) return `Every ${DAYS[Number(dow) % 7]} at ${clock(h, m)} ${z}`;
    if (/^[\d,]+$/.test(dow)) return `Every ${dow.split(',').map(d => DAYS[Number(d) % 7]).join(' and ')} at ${clock(h, m)} ${z}`;
  }
  return expr;
};
const when = (d) => {
  if (!d) return 'never yet';
  const t = new Date(d);
  const mins = Math.round((Date.now() - t) / 60000);
  const at = t.toLocaleString('en-CA', { timeZone: 'America/Halifax', weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  const ago = mins < 1 ? 'just now' : mins < 60 ? `${mins} min ago` : mins < 1440 ? `${Math.round(mins / 60)} h ago` : `${Math.round(mins / 1440)} days ago`;
  return `${at} AT (${ago})`;
};
const errText = (err, fallback) => err.response?.data?.error || fallback;

const btn = 'text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors disabled:opacity-50';
const btnPlain = `${btn} border-gray-200 text-reset-brown hover:border-reset-terracotta`;
const btnMain = `${btn} border-reset-terracotta bg-reset-terracotta text-white hover:opacity-90`;

// ─── Side panel: who is at a step ────────────────────────────────────────────
function WhoPanel({ title, people, loading, error, onClose, onTakeOut }) {
  return (
    <div className="fixed inset-0 z-40 flex justify-end" role="dialog" aria-label={title}>
      <button className="absolute inset-0 bg-black/20" onClick={onClose} aria-label="Close" />
      <div className="relative w-full max-w-sm h-full bg-white shadow-xl overflow-y-auto p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <p className="text-sm font-semibold text-reset-brown">{title}</p>
          <button onClick={onClose} className="text-xs text-gray-400 hover:text-reset-brown shrink-0">Close</button>
        </div>
        {loading && <p className="text-sm text-gray-400">Loading…</p>}
        {error && <p className="text-sm text-gray-600">{error}</p>}
        {!loading && !error && people.length === 0 && <p className="text-sm text-gray-500">Nobody at this step right now.</p>}
        {!loading && !error && people.length > 0 && (
          <>
            <p className="text-xs text-gray-400 mb-2">{people.length} {people.length === 1 ? 'person' : 'people'}</p>
            <ul className="divide-y divide-gray-100">
              {people.map((p, i) => (
                <li key={p.pid || p.id || i} className="py-2 flex items-center gap-2">
                  <span className="flex-1 min-w-0">
                    {p.id
                      ? <Link to={`/admin/members/${p.id}`} className="block text-sm text-reset-brown hover:text-reset-terracotta hover:underline truncate">{p.name}</Link>
                      : <span className="block text-sm text-reset-brown truncate">{p.name}</span>}
                    {p.detail && <span className="block text-xs text-gray-500">{p.detail}</span>}
                  </span>
                  {onTakeOut && p.pid && (
                    <button onClick={() => onTakeOut(p)} className="text-[11px] text-gray-400 hover:text-reset-brown shrink-0">Take out</button>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Step bubbles ────────────────────────────────────────────────────────────
function StepBubbles({ steps, onCount }) {
  return (
    <ol className="space-y-0">
      {steps.map((s, i) => (
        <li key={i} className="relative pl-6">
          {i < steps.length - 1 && <span className="absolute left-[7px] top-4 bottom-0 w-px bg-gray-200" aria-hidden="true" />}
          <span className={`absolute left-0 top-2.5 w-[15px] h-[15px] rounded-full border-2 ${s.type === 'start' ? 'border-reset-terracotta bg-reset-terracotta/20' : 'border-gray-300 bg-white'}`} aria-hidden="true" />
          <div className="mb-2 rounded-xl border border-gray-100 bg-reset-cream/60 px-3 py-2 flex items-start gap-3">
            <span className="flex-1 min-w-0">
              <span className="block text-[10px] font-semibold uppercase tracking-wide text-gray-400">{s.label || STEP_LABEL[s.type] || 'Step'}</span>
              <span className="block text-xs text-reset-brown leading-relaxed">{s.text}</span>
              {s.problem && <span className="block text-[11px] text-gray-500 mt-0.5">Could not count: {s.problem}</span>}
            </span>
            {s.count != null && (
              <button onClick={() => onCount(i, s)} title="See who"
                className="shrink-0 min-w-[2.5rem] text-center rounded-full bg-white border border-reset-terracotta/40 px-2.5 py-1 text-sm font-semibold text-reset-terracotta hover:bg-reset-terracotta hover:text-white">
                {s.count}
              </button>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}

// ─── One built-in automation, opened ─────────────────────────────────────────
function BuiltInDetail({ a, onChanged, openWho }) {
  const [steps, setSteps] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const loadSteps = useCallback(async () => {
    try { setErr(null); setSteps((await api.get('/automations/steps', { params: { key: a.key } })).data.steps); }
    catch (e) { setErr(errText(e, 'Could not load the steps.')); }
  }, [a.key]);
  useEffect(() => { loadSteps(); }, [loadSteps]);

  const runNow = async () => {
    setBusy(true); setMsg(null);
    try { await api.post('/automations/run-now', { key: a.key }); setMsg('Started. Press Refresh in a minute to see how it went.'); }
    catch (e) { setMsg(errText(e, 'Could not start it.')); }
    finally { setBusy(false); }
  };
  const togglePause = async () => {
    if (!a.paused && !window.confirm(`Pause "${a.name}"? It won't run again until you press Resume.`)) return;
    setBusy(true); setMsg(null);
    try { await api.post('/automations/pause', { key: a.key, paused: !a.paused }); await onChanged(); }
    catch (e) { setMsg(errText(e, 'Could not change it.')); }
    finally { setBusy(false); }
  };

  const shown = (steps || []).map(s => s.type === 'start' && a.kind === 'scheduled'
    ? { ...s, text: [describeSchedule(a.schedule, a.timezone), s.text].filter(Boolean).join('. ') }
    : s);

  return (
    <div className="mt-3 ml-5 space-y-3">
      {a.what && <p className="text-xs text-gray-600">{a.what}</p>}
      {a.canPause && (
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={runNow} disabled={busy || a.paused} className={btnPlain}>Run now</button>
          <button onClick={togglePause} disabled={busy} className={a.paused ? btnMain : btnPlain}>{a.paused ? 'Resume' : 'Pause'}</button>
          {a.paused && <span className="text-xs text-gray-500">Paused {when(a.pausedAt)}.</span>}
        </div>
      )}
      {!a.canPause && <p className="text-[11px] text-gray-400">This one runs when it happens (part of joining), so it can't be paused here. Stop all emails stops its emails.</p>}
      {msg && <p className="text-xs text-reset-brown">{msg}</p>}
      {err && <p className="text-xs text-gray-600">{err}</p>}
      {!steps && !err && <p className="text-xs text-gray-400">Loading the steps…</p>}
      {steps && <StepBubbles steps={shown} onCount={(i, s) => openWho({ title: s.text, load: async () => (await api.get('/automations/who', { params: { key: a.key, step: i } })).data.people })} />}
      <div className="text-xs text-gray-600 space-y-1.5">
        {a.note && <p className="text-reset-brown">{a.note.startsWith('Was due') ? `It was due ${when(a.expectedAt)} and didn't run.` : a.note}</p>}
        {a.kind === 'scheduled' && a.runsToday > 0 && <p>Ran {a.runsToday} time{a.runsToday === 1 ? '' : 's'} today.</p>}
        {a.problems7d > 0 && <p>Problems in the last 7 days: {a.problems7d}. Last one {when(a.lastProblemAt)}: {a.lastProblem}</p>}
        {a.problems7d === 0 && <p>No problems in the last 7 days.</p>}
      </div>
    </div>
  );
}

// ─── Your own automations: the builder ───────────────────────────────────────
const TRIGGERS = [
  { key: 'joins', label: 'When someone joins' },
  { key: 'tag_added', label: 'When someone gets a tag' },
  { key: 'cancels', label: 'When someone cancels (leaves)' },
  { key: 'days_after_join', label: 'A number of days after someone joins' },
  { key: 'days_before_renewal', label: 'A number of days before their membership renews' },
  { key: 'birthday', label: 'On their birthday' },
];
const STEP_KINDS = [
  { type: 'wait', label: 'Wait', blank: { type: 'wait', days: 1, hours: 0 } },
  { type: 'email', label: 'Send an email', blank: { type: 'email', subject: '', body: '' } },
  { type: 'text', label: 'Send a text', blank: { type: 'text', body: '' } },
  { type: 'add_tag', label: 'Add a tag', blank: { type: 'add_tag', tag: '' } },
  { type: 'remove_tag', label: 'Remove a tag', blank: { type: 'remove_tag', tag: '' } },
  { type: 'todo', label: 'Make a Team365 To-Do', blank: { type: 'todo', title: '', body: '' } },
];
const stepWords = (s) => ({
  wait: `Wait ${s.days || 0} day${Number(s.days) === 1 ? '' : 's'}${Number(s.hours) ? ` and ${s.hours} hour${Number(s.hours) === 1 ? '' : 's'}` : ''}`,
  email: `Send an email: "${s.subject}"`,
  text: `Send a text: "${String(s.body || '').slice(0, 80)}${String(s.body || '').length > 80 ? '…' : ''}"`,
  add_tag: `Add the tag "${s.tag}"`,
  remove_tag: `Remove the tag "${s.tag}"`,
  todo: `Make a Team365 To-Do: "${s.title}"`,
}[s.type] || s.type);

const input = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-reset-brown focus:border-reset-terracotta focus:outline-none';

function Builder({ start, tags, onSaved, onCancel }) {
  const [form, setForm] = useState(() => start ? {
    name: start.name, trigger_type: start.trigger_type, trigger_value: start.trigger_value || '', steps: start.steps, include_practice: start.include_practice,
  } : { name: '', trigger_type: 'joins', trigger_value: '', steps: [], include_practice: false });
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const setStep = (i, patch) => setForm(f => ({ ...f, steps: f.steps.map((s, j) => j === i ? { ...s, ...patch } : s) }));
  const move = (i, d) => setForm(f => {
    const steps = [...f.steps]; const j = i + d;
    if (j < 0 || j >= steps.length) return f;
    [steps[i], steps[j]] = [steps[j], steps[i]];
    return { ...f, steps };
  });
  const save = async () => {
    setSaving(true); setError(null);
    try {
      const res = start ? await api.put(`/automations/custom/${start.id}`, form) : await api.post('/automations/custom', form);
      onSaved(res.data.id);
    } catch (e) { setError(errText(e, 'Could not save it.')); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl border border-gray-200 p-4 space-y-4">
      <label className="block">
        <span className="block text-xs font-semibold text-gray-500 mb-1">Name</span>
        <input className={input} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Two weeks in: check on new members" />
      </label>

      <div>
        <span className="block text-xs font-semibold text-gray-500 mb-1">What starts it</span>
        <select className={input} value={form.trigger_type} onChange={e => setForm({ ...form, trigger_type: e.target.value, trigger_value: '' })}>
          {TRIGGERS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        {form.trigger_type === 'tag_added' && (
          <div className="mt-2">
            <input className={input} list="automation-tags" value={form.trigger_value} onChange={e => setForm({ ...form, trigger_value: e.target.value })} placeholder="Type or choose a tag" />
          </div>
        )}
        {['days_after_join', 'days_before_renewal'].includes(form.trigger_type) && (
          <div className="mt-2 flex items-center gap-2">
            <input type="number" min="0" className={`${input} w-24`} value={form.trigger_value} onChange={e => setForm({ ...form, trigger_value: e.target.value })} />
            <span className="text-sm text-gray-600">days</span>
          </div>
        )}
        {form.trigger_type === 'birthday' && <p className="text-[11px] text-gray-400 mt-1">Uses the birthday on each member's record. Members without one are left out.</p>}
      </div>

      <div>
        <span className="block text-xs font-semibold text-gray-500 mb-2">Then, in this order</span>
        {form.steps.length === 0 && <p className="text-xs text-gray-400 mb-2">No steps yet. Add the first one below.</p>}
        <ol className="space-y-2">
          {form.steps.map((s, i) => (
            <li key={i} className="rounded-xl bg-reset-cream/60 border border-gray-100 p-3 space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 flex-1">Step {i + 1} · {STEP_KINDS.find(k => k.type === s.type)?.label}</span>
                <button onClick={() => move(i, -1)} disabled={i === 0} className="text-xs text-gray-400 hover:text-reset-brown disabled:opacity-30" aria-label="Move up">↑</button>
                <button onClick={() => move(i, 1)} disabled={i === form.steps.length - 1} className="text-xs text-gray-400 hover:text-reset-brown disabled:opacity-30" aria-label="Move down">↓</button>
                <button onClick={() => setForm(f => ({ ...f, steps: f.steps.filter((_, j) => j !== i) }))} className="text-xs text-gray-400 hover:text-reset-brown">Remove</button>
              </div>
              {s.type === 'wait' && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <input type="number" min="0" className={`${input} w-20`} value={s.days} onChange={e => setStep(i, { days: e.target.value })} /> days
                  <input type="number" min="0" max="23" className={`${input} w-20`} value={s.hours} onChange={e => setStep(i, { hours: e.target.value })} /> hours
                </div>
              )}
              {s.type === 'email' && (
                <>
                  <input className={input} value={s.subject} onChange={e => setStep(i, { subject: e.target.value })} placeholder="Subject" />
                  <textarea className={input} rows={6} value={s.body} onChange={e => setStep(i, { body: e.target.value })} placeholder="The words. It starts with Hi and their first name by itself." />
                  <p className="text-[11px] text-gray-400">Type {'{first_name}'} anywhere to use their first name. Never goes to anyone who switched off emails from Team365.</p>
                </>
              )}
              {s.type === 'text' && (
                <>
                  <textarea className={input} rows={3} value={s.body} onChange={e => setStep(i, { body: e.target.value })} placeholder="The text" maxLength={600} />
                  <p className="text-[11px] text-gray-400">{'{first_name}'} works here too. The "Reply Stop" line is added by itself. Only goes to members with a mobile number who haven't said stop.</p>
                </>
              )}
              {(s.type === 'add_tag' || s.type === 'remove_tag') && (
                <input className={input} list="automation-tags" value={s.tag} onChange={e => setStep(i, { tag: e.target.value })} placeholder="Type or choose a tag" />
              )}
              {s.type === 'todo' && (
                <>
                  <input className={input} value={s.title} onChange={e => setStep(i, { title: e.target.value })} placeholder="To-Do title, e.g. Call {first_name}" />
                  <textarea className={input} rows={3} value={s.body} onChange={e => setStep(i, { body: e.target.value })} placeholder="Notes (optional)" />
                </>
              )}
            </li>
          ))}
        </ol>
        <div className="flex flex-wrap gap-2 mt-3">
          {STEP_KINDS.map(k => (
            <button key={k.type} onClick={() => setForm(f => ({ ...f, steps: [...f.steps, { ...k.blank }] }))} className={btnPlain}>+ {k.label}</button>
          ))}
        </div>
      </div>

      <label className="flex items-center gap-2 text-xs text-gray-600">
        <input type="checkbox" checked={form.include_practice} onChange={e => setForm({ ...form, include_practice: e.target.checked })} />
        Include practice accounts (for trying it out)
      </label>

      <datalist id="automation-tags">{tags.map(t => <option key={t.tag} value={t.tag}>{`${t.n} member${t.n === 1 ? '' : 's'}`}</option>)}</datalist>

      {error && <p className="text-xs text-reset-brown">{error}</p>}
      <div className="flex gap-2">
        <button onClick={save} disabled={saving} className={btnMain}>{saving ? 'Saving…' : 'Save'}</button>
        <button onClick={onCancel} className={btnPlain}>Cancel</button>
      </div>
      {!start && <p className="text-[11px] text-gray-400">It's saved switched off. Switch it on when you're ready; from then on, everyone who qualifies goes through it.</p>}
    </div>
  );
}

function CustomDetail({ id, tags, onChanged, openWho, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setError(null); setData((await api.get(`/automations/custom/${id}`)).data); }
    catch (e) { setError(errText(e, 'Could not load it.')); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (error) return <p className="text-xs text-gray-600 mt-3">{error}</p>;
  if (!data) return <p className="text-xs text-gray-400 mt-3">Loading…</p>;
  const a = data.automation;

  if (editing) {
    return <div className="mt-3"><Builder start={a} tags={tags} onCancel={() => setEditing(false)} onSaved={async () => { setEditing(false); await load(); onChanged(); }} /></div>;
  }

  const toggle = async () => {
    if (!a.is_on && !window.confirm(a.starts_from
      ? `Switch "${a.name}" back on? Everyone waiting carries on, and anyone who qualified while it was off is picked up.`
      : `Switch "${a.name}" on? From now on, everyone who qualifies goes through these steps. Nobody who qualified before now is included.`)) return;
    setBusy(true);
    try { await api.post(`/automations/custom/${a.id}/on`, { on: !a.is_on }); await load(); onChanged(); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${a.name}"? Everyone in it stops where they are. This can't be undone.`)) return;
    await api.delete(`/automations/custom/${a.id}`);
    onClose(); onChanged();
  };
  const people = (list) => list.map(p => ({ pid: p.id, id: p.member_id, name: p.name || '(no name)', detail: p.last_problem || undefined }));
  const takeOut = async (p) => {
    if (!window.confirm(`Take ${p.name} out of "${a.name}"? Nothing more from it goes to them.`)) return;
    await api.post(`/automations/custom/${a.id}/people/${p.pid}/stop`);
    await load();
    return true;
  };

  const bubbles = [
    { type: 'start', label: 'Starts', text: a.trigger_words, count: null },
    ...data.steps.map(s => ({ type: s.type === 'wait' ? 'next' : 'send', label: s.type === 'wait' ? 'Waits' : 'Does', text: stepWords(s), count: s.people.length, list: s.people })),
    { type: 'next', label: 'Finished', text: 'Went through every step.', count: data.finished.length, list: data.finished },
  ];
  if (data.stopped.length) bubbles.push({ type: 'next', label: 'Taken out', text: 'Taken out, or no longer a member.', count: data.stopped.length, list: data.stopped });

  return (
    <div className="mt-3 ml-5 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={toggle} disabled={busy} className={a.is_on ? btnPlain : btnMain}>{a.is_on ? 'Switch off' : 'Switch on'}</button>
        <button onClick={() => setEditing(true)} className={btnPlain}>Change it</button>
        <button onClick={remove} className={btnPlain}>Delete</button>
        <span className="text-xs text-gray-500">{a.is_on ? `On since ${when(a.updated_at)}` : 'Off'}</span>
      </div>
      <StepBubbles steps={bubbles} onCount={(i, s) => openWho({ title: s.text, people: people(s.list), onTakeOut: s.label === 'Finished' || s.label === 'Taken out' ? null : takeOut })} />
      {data.problems.length > 0 && (
        <div className="text-xs text-gray-600">
          <p className="font-medium text-reset-brown mb-1">Things that didn't go</p>
          <ul className="space-y-0.5">{data.problems.map(p => <li key={p.id}>{p.name}: {p.last_problem}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

// ─── The page ────────────────────────────────────────────────────────────────
export default function AdminAutomations() {
  const [data, setData] = useState(null);
  const [custom, setCustom] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  const [openCustom, setOpenCustom] = useState(null);
  const [making, setMaking] = useState(false);
  const [panel, setPanel] = useState(null);
  const [notice, setNotice] = useState(null);
  const [restarting, setRestarting] = useState(false);

  const load = useCallback(async () => {
    try {
      setError(null);
      const [a, c] = await Promise.all([api.get('/automations'), api.get('/automations/custom')]);
      setData(a.data); setCustom(c.data);
    } catch (err) { setError(errText(err, 'Could not load the automations.')); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openWho = async ({ title, load: loader, people, onTakeOut }) => {
    if (people) { setPanel({ title, people, onTakeOut }); return; }
    setPanel({ title, loading: true, people: [] });
    try { setPanel({ title, people: await loader() }); }
    catch (e) { setPanel({ title, people: [], error: errText(e, 'Could not load who is at this step.') }); }
  };

  const flip = async (which) => {
    const stopped = !data.switches[which].stopped;
    const words = which === 'emails'
      ? (stopped ? 'Stop ALL emails from 365reset? Nothing goes out (daily emails, onboarding, reminders, everything) until you start them again. Sign-in emails still go, so nobody is locked out. Anything missed in the meantime catches up when you start again, where the automation does that.' : 'Start emails again?')
      : (stopped ? 'Stop ALL texts from 365reset? Nothing goes out by text until you start them again.' : 'Start texts again?');
    if (!window.confirm(words)) return;
    try { await api.post('/automations/switch', { which, stopped }); await load(); }
    catch (e) { setNotice(errText(e, 'Could not change the switch.')); }
  };

  const restart = async () => {
    if (!window.confirm('Restart the app? Everyone using 365reset sees a short pause (about 20 seconds), then it carries on. Use this when something is stuck.')) return;
    setRestarting(true); setNotice('Restarting… this takes about 20 seconds.');
    try { await api.post('/automations/restart'); } catch { /* the app is going down; expected */ }
    await new Promise(r => setTimeout(r, 5000));
    for (let i = 0; i < 40; i++) {
      try { await api.get('/automations'); setNotice('The app restarted and is running.'); setRestarting(false); await load(); return; }
      catch { await new Promise(r => setTimeout(r, 3000)); }
    }
    setRestarting(false);
    setNotice('The app has not come back after 2 minutes. Make a To-Do for Claude, or ask your host to restart it.');
  };

  if (error) return <p className="text-sm text-gray-600">{error}</p>;
  if (!data || !custom) return <p className="text-sm text-gray-400">Loading…</p>;

  const list = data.automations;
  const needLook = list.filter(a => a.status === 'problem' || a.status === 'missed').length;
  const sw = data.switches || { emails: {}, texts: {} };

  return (
    <div className="max-w-3xl space-y-5">
      <div className="bg-white rounded-2xl shadow-sm p-5">
        <h2 className="font-serif text-xl font-bold text-reset-brown">Automations</h2>
        <p className="text-sm text-gray-600 mt-1">
          Everything 365reset does by itself, and whether it's working.
          {needLook === 0
            ? ' All of them are working.'
            : ` ${needLook} need${needLook === 1 ? 's' : ''} a look. Each one also has a Team365 To-Do.`}
        </p>
        <p className="text-xs text-gray-400 mt-2">Checked every hour. Anything that has a problem or doesn't run on time becomes a To-Do, so you don't need to watch this page. Open any one to see its steps, who is at each step, and its buttons.</p>
        <div className="flex flex-wrap gap-2 mt-4">
          <button onClick={load} className={btnPlain}>Refresh</button>
          <button onClick={() => flip('emails')} className={sw.emails.stopped ? btnMain : btnPlain}>{sw.emails.stopped ? 'Start emails again' : 'Stop all emails'}</button>
          <button onClick={() => flip('texts')} className={sw.texts.stopped ? btnMain : btnPlain}>{sw.texts.stopped ? 'Start texts again' : 'Stop all texts'}</button>
          <button onClick={restart} disabled={restarting} className={btnPlain}>Restart the app</button>
        </div>
        {(sw.emails.stopped || sw.texts.stopped) && (
          <p className="mt-3 text-xs text-reset-brown bg-reset-gold/25 rounded-lg px-3 py-2">
            {sw.emails.stopped && `Emails are stopped (since ${when(sw.emails.changedAt)}). Sign-in emails still go. `}
            {sw.texts.stopped && `Texts are stopped (since ${when(sw.texts.changedAt)}).`}
          </p>
        )}
        {notice && <p className="mt-3 text-xs text-reset-brown">{notice}</p>}
      </div>

      <div className="bg-white rounded-2xl shadow-sm divide-y divide-gray-100">
        {list.map(a => {
          const st = STATUS[a.status] || STATUS.ok;
          const isOpen = open === a.key;
          return (
            <div key={a.key} className="p-4">
              <button onClick={() => setOpen(isOpen ? null : a.key)} className="w-full text-left flex items-start gap-3">
                <span className={`mt-1.5 w-2.5 h-2.5 rounded-full shrink-0 ${st.dot}`} aria-hidden="true" />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-semibold text-reset-brown">{a.name}</span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    {st.text} · {a.kind === 'event' ? 'When it happens' : describeSchedule(a.schedule, a.timezone)} · last ran {when(a.lastRunAt)}
                  </span>
                </span>
                <span className="text-xs text-gray-400 shrink-0">{isOpen ? 'Less' : 'Open'}</span>
              </button>
              {isOpen && <BuiltInDetail a={a} onChanged={load} openWho={openWho} />}
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-reset-brown">Your own automations</p>
            <p className="text-xs text-gray-500 mt-0.5">Made here from building blocks: what starts it, then wait, send an email or text, add or remove a tag, or make a To-Do.</p>
          </div>
          {!making && <button onClick={() => { setMaking(true); setOpenCustom(null); }} className={`${btnMain} shrink-0`}>Make a new one</button>}
        </div>
        {making && (
          <div className="mt-4">
            <Builder tags={custom.tags} onCancel={() => setMaking(false)} onSaved={async (id) => { setMaking(false); await load(); setOpenCustom(id); }} />
          </div>
        )}
        {custom.automations.length === 0 && !making && <p className="text-xs text-gray-400 mt-3">None yet.</p>}
        {custom.automations.length > 0 && (
          <div className="divide-y divide-gray-100 mt-3">
            {custom.automations.map(c => {
              const isOpen = openCustom === c.id;
              return (
                <div key={c.id} className="py-3">
                  <button onClick={() => setOpenCustom(isOpen ? null : c.id)} className="w-full text-left flex items-start gap-3">
                    <span className={`mt-1.5 w-2.5 h-2.5 rounded-full shrink-0 ${c.is_on ? 'bg-reset-sage' : 'bg-gray-300'}`} aria-hidden="true" />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold text-reset-brown">{c.name}</span>
                      <span className="block text-xs text-gray-500 mt-0.5">
                        {c.is_on ? 'On' : 'Off'} · {c.trigger_words} · {c.steps.length} step{c.steps.length === 1 ? '' : 's'} · {c.in_progress} going through it · {c.finished} finished{c.problems ? ` · ${c.problems} didn't go` : ''}
                      </span>
                    </span>
                    <span className="text-xs text-gray-400 shrink-0">{isOpen ? 'Less' : 'Open'}</span>
                  </button>
                  {isOpen && <CustomDetail id={c.id} tags={custom.tags} onChanged={load} openWho={openWho} onClose={() => setOpenCustom(null)} />}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {data.recentProblems.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <p className="text-sm font-semibold text-reset-brown mb-2">Problems in the last 7 days</p>
          <ul className="space-y-1.5">
            {data.recentProblems.map((p, i) => (
              <li key={i} className="text-xs text-gray-600"><span className="font-medium text-reset-brown">{p.name}</span> · {when(p.created_at)} · {p.problem}</li>
            ))}
          </ul>
        </div>
      )}

      {panel && (
        <WhoPanel {...panel} onClose={() => setPanel(null)}
          onTakeOut={panel.onTakeOut ? async (p) => { if (await panel.onTakeOut(p)) setPanel(null); } : null} />
      )}
    </div>
  );
}
